import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import Card from '@mui/material/Card/Card';
import CardContent from '@mui/material/CardContent/CardContent';
import CardActionArea from '@mui/material/CardActionArea/CardActionArea';
import Stack from '@mui/material/Stack/Stack';
import Box from '@mui/material/Box/Box';
import Typography from '@mui/material/Typography/Typography';
import Button from '@mui/material/Button/Button';
import Chip from '@mui/material/Chip/Chip';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import { HackathonPayload } from '../../services/hackathon.service';
import { useSessions } from '../../hooks/useSessions';
import { useAuth } from '../../services/useAuth';
import { categoryLabels, hasAnyRole, roleLabels, sessionRoles } from '../../utils/sessions';

dayjs.extend(relativeTime);

// details changed within this window are flagged so people notice a new room or meeting link
const RECENT_UPDATE_MS = 2 * 60 * 60 * 1000;

/**
 * Hackathon page summary of judging sessions; the signed-in user's own sessions come first.
 */
export const SessionsCard = ({
    hackathonId,
    hackathon,
    isOrganizer
}: {
    // as in the URL (slug or id), shared with the other session views for caching
    hackathonId: string,
    hackathon: HackathonPayload,
    isOrganizer: boolean
}) => {
    const { sessions } = useSessions(hackathonId);
    const { user } = useAuth();
    const email = user ? user.email : undefined;
    const location = useLocation();

    // notifications link to #sessions; the router doesn't scroll to hashes itself
    useEffect(() => {
        if (location.hash === '#sessions' && sessions.length > 0) {
            document.getElementById('sessions')?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [location.hash, sessions.length]);

    // one room ("Main room" by default) needs no sessions section; organizers can add rooms
    if (sessions.length <= 1) {
        if (!isOrganizer) {
            return null;
        }
        return (
            <Card id="sessions" sx={{ borderStyle: 'dashed' }}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <MeetingRoomIcon color="primary" />
                            <div>
                                <Typography variant="subtitle1">Running several rooms at once?</Typography>
                                <Typography variant="body2" color="text.secondary">Add rooms to split judging, each with its own judges, teams and categories.</Typography>
                            </div>
                        </Stack>
                        <Button variant="outlined" component={Link} to={`/h/${hackathon.slug}/sessions`}>Add another room</Button>
                    </Stack>
                </CardContent>
            </Card>
        )
    }

    const withRoles = sessions.map(session => ({ session, roles: sessionRoles(session, hackathon, email) }));
    const ordered = [...withRoles.filter(s => hasAnyRole(s.roles)), ...withRoles.filter(s => !hasAnyRole(s.roles))];

    return (
        <Box id="sessions" sx={{ scrollMarginTop: 88 }}>
            <Stack spacing={2}>
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography variant="h3" component="h2">Sessions</Typography>
                        <Chip size="small" label={sessions.length} />
                    </Stack>
                    {isOrganizer && <Button component={Link} to={`/h/${hackathon.slug}/sessions`}>Manage</Button>}
                </Stack>
                <Box sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }
                }}>
                    {
                        ordered.map(({ session, roles }) => {
                            const updated = session.descriptionUpdatedAt;
                            const recent = !!updated && Date.now() - updated.getTime() < RECENT_UPDATE_MS;
                            const mine = hasAnyRole(roles);
                            return (
                                <Card key={session._id} sx={mine ? { borderColor: 'primary.main', borderWidth: 2 } : undefined}>
                                    <CardActionArea component={Link} to={`/h/${hackathon.slug}/s/${session.slug}`} sx={{ height: '100%', color: 'text.primary', textDecoration: 'none' }}>
                                        <CardContent>
                                            <Stack spacing={1}>
                                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                                    <Typography variant="h5" component="h3">{session.name}</Typography>
                                                    {recent && updated && <Chip size="small" color="secondary" label={`Updated ${dayjs(updated).fromNow()}`} />}
                                                </Stack>
                                                {
                                                    mine && (
                                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                            {roleLabels(roles).map(label => <Chip key={label} size="small" color="primary" label={`You're ${label.toLowerCase()}`} />)}
                                                        </Stack>
                                                    )
                                                }
                                                <Typography variant="body2" color="text.secondary">
                                                    {session.ideas.length} team{session.ideas.length === 1 ? '' : 's'} · {session.judges.length} judge{session.judges.length === 1 ? '' : 's'}
                                                </Typography>
                                                {
                                                    session.categories.length > 0 && (
                                                        <Typography variant="body2" color="text.secondary" noWrap>{categoryLabels(hackathon, session.categories).join(' · ')}</Typography>
                                                    )
                                                }
                                                {updated && !recent && <Typography variant="caption" color="text.secondary">Details updated {dayjs(updated).fromNow()}</Typography>}
                                            </Stack>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            )
                        })
                    }
                </Box>
            </Stack>
        </Box>
    )
}
